import type { IBankAccountPaymentMethod } from "../interfaces/payment-method";
import { payerCodesEquivalent } from "./payerCode";

export interface OfferableBankOption {
  payerCode: string | null;
}

/**
 * Saved vault accounts that can be one-tapped on this operation.
 * Country filtering belongs to `useBankAccounts`. This only gates payer
 * offerability so we never pin a bank the engine cannot quote.
 *
 * - No BANK_ACCOUNT options → empty.
 * - Options with payerCode → account.bankName must match one of them.
 * - Generic method SKU (no payerCode) → account.bankName must match catalog
 *   AVAILABLE codes. `catalogPayerCodes === null` means catalog still loading;
 *   return empty so the caller can show a skeleton instead of a stale list.
 */
export const offerableSavedBankAccounts = (
  accounts: IBankAccountPaymentMethod[],
  methodOptions: OfferableBankOption[],
  catalogPayerCodes: readonly string[] | null
): IBankAccountPaymentMethod[] => {
  if (methodOptions.length === 0) return [];

  const fromOptions = [
    ...new Set(
      methodOptions
        .map((o) => o.payerCode?.trim())
        .filter((code): code is string => Boolean(code))
    ),
  ];

  const allowed =
    fromOptions.length > 0 ? fromOptions : (catalogPayerCodes ?? null);
  if (allowed == null || allowed.length === 0) return [];

  return accounts.filter((account) =>
    allowed.some((code) => payerCodesEquivalent(code, account.bankName))
  );
};
